import * as React from 'react';

import { Loader } from '../../../Loader/loader';
import { Alert } from '../../../Alert/alert';
import { ConfirmModal } from '../../../ConfirmModal/confirmModal';
import { useActions as useViewActions } from '../../viewActions';
import { useActions as useEFSActions } from './EFSEditModeActions';
import { useSelector } from '../../../../utils/useSelector';
import { EditMode } from '../../EditMode/editMode';
import { useBlocker } from 'react-router-dom';

interface EFSEditModeProps {}

export const EFSEditMode = ({}: EFSEditModeProps) => {
  const editMode = useSelector(s => s.view?.editMode);
  const tempFilters = useSelector(s => s.view?.tempFilters);
  const hasUnsavedChanges = useSelector(state => state.view.hasUnsavedChanges);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [showFailedModal, setShowFailedModal] = React.useState(false);

  const { setUnsavedChangesBoolean, setEditMode, setFilters, setTempFilters } =
    useViewActions();
  const { saveEfsData, emptyEFSItemData } = useEFSActions();

  const handleSaveEsfData = async () => {
    const response = await saveEfsData();
    if (response) setShowSuccessModal(true);
    else {
      setShowFailedModal(true);
    }
  };

  const handleExitEditMode = () => {
    hasUnsavedChanges ? setShowConfirmModal(true) : setEditMode(undefined);
  };

  let blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (editMode && hasUnsavedChanges) {
      let urlChanged = currentLocation.pathname != nextLocation.pathname;
      if (!urlChanged) {
        const currentSearch = new URLSearchParams(currentLocation.search);
        const nextSearch = new URLSearchParams(nextLocation.search);
        const hasTempFilters = nextSearch.get('tempFilters') != null;
        const ignoreParams = ['id', 'c', 'm', 'tempFilters'];
        ignoreParams.forEach(param => {
          currentSearch.delete(param);
          nextSearch.delete(param);
        });
        urlChanged =
          currentSearch.toString() !== nextSearch.toString() || hasTempFilters;
      }

      if (urlChanged) {
        setShowConfirmModal(true);
        return true;
      }
    }
    return false;
  });

  const handleNavigation = () => {
    setShowConfirmModal(false);
    if (blocker.proceed) {
      if (tempFilters) {
        setFilters(tempFilters);
        setTempFilters(null);
      }
      blocker.proceed();
    }
  };
  const handleStayOnView = () => {
    setShowConfirmModal(false);
    if (blocker.reset) blocker.reset();
  };

  if (!editMode) return null;

  return (
    <>
      {showConfirmModal && (
        <ConfirmModal
          header="Are you sure?"
          onCancel={() => {
            handleNavigation();
            setUnsavedChangesBoolean(false);
            setEditMode(false);
            emptyEFSItemData();
          }}
          onConfirm={handleStayOnView}
          label={'Stay on this view'}
          cancelLabel={'Discard changes'}
        >
          <Loader />
          <Alert />
          <div>
            You have pending changes that have not been saved on this view.
            Please click 'Save' in the banner to save changes before navigating
            away from this view.
          </div>
        </ConfirmModal>
      )}
      {showSuccessModal && (
        <ConfirmModal
          header="Save Successful"
          onCancel={() => {
            setShowSuccessModal(false);
            setEditMode(undefined);
          }}
          cancelLabel="Close"
        >
          <div>
            The display order has been saved successfully. This may take up to a
            minute to reflect correctly within CPP Planning.{' '}
            <strong>
              Please Note: the display order of an item will not be reflected on
              <a href="http://Gartner.com"> Gartner.com</a> until the item has
              reached the Published status.
            </strong>
          </div>
        </ConfirmModal>
      )}
      {showFailedModal && (
        <ConfirmModal
          header="Save Failed"
          onCancel={() => setShowFailedModal(false)}
          cancelLabel="Close"
        >
          <div>
            Save Failed. An unexpected error occurred, please try again. If the
            issue persists, please reach out to CPPHelp@gartner.com.
          </div>
        </ConfirmModal>
      )}
      <EditMode
        handleSave={handleSaveEsfData}
        handleExit={handleExitEditMode}
        description={'Drag and drop to set the display order.'}
      />
    </>
  );
};
